import { apiUrl } from '../config/api';

// Function to refresh access token using refresh token
export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refresh_token');
  
  if (!refreshToken) {
    return null;
  }
  
  try {
    const response = await fetch(`${apiUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    
    if (!response.ok) {
      // Refresh token is invalid or expired
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      return null;
    }
    
    const data = await response.json();
    
    if (!data.success || !data.data?.token) {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      return null;
    }
    
    // Save new access token
    localStorage.setItem('token', data.data.token);
    
    // If new refresh token is provided, save it (token rotation when close to expiration)
    if (data.data.refresh_token) {
      localStorage.setItem('refresh_token', data.data.refresh_token);
    }
    
    return data.data.token;
    } catch (error) {
      // Don't log full error object - may contain sensitive token data
      console.error('Error refreshing token:', error instanceof Error ? error.message : 'Unknown error');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      return null;
    }
  };

export const authProvider = {
  login: ({ username, password }: { username: string; password: string }) => {
    return fetch(`${apiUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: username, password }),
    })
    .then(async response => {
      const data = await response.json();
      
      if (!response.ok) {
        let errorMessage = 'Incorrect email or password';
        if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (response.status === 400) {
          errorMessage = 'Incorrect email or password';
        } else {
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      return data;
    })
    .then(response => {
      if (!response.success) {
        throw new Error(response.message || 'Error login');
      }
      
      const { data } = response;
      
      if (data.user.user_type !== 'admin') {
        throw new Error('You are not an administrator. Access is denied.');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));
    })
    .catch(error => {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      throw error;
    });
  },
  
  logout: async () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refresh_token');
    
    // Try to revoke refresh token on server
    if (token && refreshToken) {
      try {
        await fetch(`${apiUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch (error) {
        // Ignore errors during logout, but log message only (no sensitive data)
        console.error('Error during logout:', error instanceof Error ? error.message : 'Unknown error');
      }
    }
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    return Promise.resolve();
  },
  
  checkAuth: () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refresh_token');
    const user = localStorage.getItem('user');
    
    // If we have refresh token but no access token, try to refresh
    if (!token && refreshToken) {
      return refreshAccessToken().then(newToken => {
        if (newToken && user) {
          try {
            const userData = JSON.parse(user);
            if (userData.user_type !== 'admin') {
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user');
              return Promise.reject();
            }
            return Promise.resolve();
          } catch (error) {
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            return Promise.reject();
          }
        }
        return Promise.reject();
      });
    }
    
    if (!token || !user) {
      return Promise.reject();
    }
    
    try {
      const userData = JSON.parse(user);
      if (userData.user_type !== 'admin') {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        return Promise.reject();
      }
      return Promise.resolve();
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      return Promise.reject();
    }
  },
  
  checkError: (error: any) => {
    if (error.status === 401 || error.status === 403) {
      return Promise.reject();
    }
    return Promise.resolve();
  },
  
  getIdentity: () => {
    const user = localStorage.getItem('user');
    return user ? Promise.resolve(JSON.parse(user)) : Promise.reject();
  },
  
  getPermissions: () => Promise.resolve(''),
};

